/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
// examples/Sidenav/SidenavCollapse/index.js
import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// react-router
import { useLocation, NavLink, Link as RouterLink } from "react-router-dom";

// @mui
import List from "@mui/material/List";
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Soft UI helpers
import SoftBox from "components/SoftBox";
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";
import { useSoftUIController } from "context";

function SidenavCollapse({
  color = "info",
  icon,
  name,
  active = false,
  noCollapse = false,
  collapse = [],          // ← hijos
  isGroup = false,
  open: openProp = false,
}) {
  //const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const childActive = collapse.some((c) => c.route && pathname === c.route);
  const [selfOpen, setSelfOpen] = useState(childActive);
  useEffect(() => { setSelfOpen(childActive); }, [childActive]);
  // si es header de grupo, usa el estado abierto que viene DEL PADRE
  const open = isGroup ? openProp : selfOpen;
  const handleToggle = () => {
    if (!isGroup) setSelfOpen((p) => !p); // grupos no se togglean desde aquí
  };

  const [controller] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;

  /* ---------- botón/filita ---------- */
  const button = (
    <ListItem component="li">
      <SoftBox
      onClick={noCollapse ? undefined : handleToggle}
      sx={(theme) => collapseItem(theme, { active, transparentSidenav })}
    >
      {/* icono */}
      <ListItemIcon
        sx={(theme) => collapseIconBox(theme, { active, transparentSidenav, color })}
      >
        {typeof icon === "string" ? (
          <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
        ) : (
          icon
        )}
      </ListItemIcon>

      {/* texto */}
      <ListItemText
        primary={name}
        sx={(theme) => collapseText(theme, { miniSidenav, transparentSidenav, active })}
      />

      {/* flecha: para header de grupo o cuando tiene hijos internos */}
      {(!noCollapse && (isGroup || collapse.length > 0)) && (
        <Icon
          sx={{
            fontSize: 16,
            ml: 0.5,
            transition: "transform .2s ease",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
          }}
        >
          expand_more
        </Icon>
      )}
      </SoftBox>
    </ListItem>
  );

  /* ---------- Header de grupo controlado: no renderiza hijos aquí ---------- */
  if (isGroup) return button;

  /* ---------- SIN hijos → devolver botón ---------- */
  if (noCollapse || collapse.length === 0) return button;

  /* ---------- CON hijos → botón + lista colapsable ---------- */
  return (
    <>
      {button}

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 3 }}>
          {collapse.map((item) => {
            const isChildActive = !!item.route && pathname === item.route;
            const Child = (
              <SidenavCollapse
                key={item.key}
                color={item.color || color}
                icon={item.icon}
                name={item.name}
                active={isChildActive}
                noCollapse={item.noCollapse}
                collapse={item.collapse || []}
              />
            );

            // externo?
            if (item.href)
              return (
                <RouterLink
                  key={item.key}
                  to={item.href}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  {Child}
                </RouterLink>
              );

            // interno
            return item.route ? (
              <NavLink
                key={item.key}
                to={item.route}
                end
                style={{ textDecoration: "none" }}
              >
                {Child}
              </NavLink>
            ) : (
              <span key={item.key}>{Child}</span>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}

/* ---------- PropTypes ---------- */
SidenavCollapse.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  collapse: PropTypes.arrayOf(PropTypes.object),
  isGroup: PropTypes.bool,
  open: PropTypes.bool,
};

export default SidenavCollapse;